import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import useLeadSubmit from '../useLeadSubmit';

export default function MultiStepFormSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'centered';

    const steps = content.steps || [];
    const submitText = content.submit_text || 'Enviar';
    const successMessage = content.success_message || 'Gracias! Te contactaremos pronto.';

    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const { submit, loading } = useLeadSubmit();

    const handleChange = (fieldName, value) => {
        setFormData(prev => ({ ...prev, [fieldName]: value }));
    };

    const handleCheckbox = (fieldName, option) => {
        setFormData(prev => {
            const current = prev[fieldName] || [];
            const updated = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
            return { ...prev, [fieldName]: updated };
        });
    };

    const handleSubmit = async () => {
        await submit({ block_type: 'multi_step_form', block_id: block.id, ...formData });
        setSubmitted(true);
    };

    const renderStepInput = (step) => {
        const options = (step.options || '').split('\n').filter(Boolean);
        const fieldName = step.field_name || `step_${currentStep}`;

        if (step.type === 'checkbox') {
            const selected = formData[fieldName] || [];
            return (
                <div className="grid gap-3">
                    {options.map((opt, i) => (
                        <label key={i} onClick={() => handleCheckbox(fieldName, opt)}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all"
                            style={{ borderColor: selected.includes(opt) ? s.accent : s.dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)', backgroundColor: selected.includes(opt) ? hexToRgba(s.accent, 0.1) : 'transparent' }}>
                            <div className="w-5 h-5 rounded border-2 flex items-center justify-center" style={{ borderColor: selected.includes(opt) ? s.accent : s.dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', backgroundColor: selected.includes(opt) ? s.accent : 'transparent' }}>
                                {selected.includes(opt) && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/></svg>}
                            </div>
                            <span style={{ color: s.text }}>{opt}</span>
                        </label>
                    ))}
                </div>
            );
        }
        if (step.type === 'textarea') {
            return <textarea value={formData[fieldName] || ''} onChange={e => handleChange(fieldName, e.target.value)} rows={4} className="w-full p-3 rounded-lg border-2 bg-transparent resize-none focus:outline-none" style={{ borderColor: hexToRgba(s.accent, 0.3), color: s.text }} placeholder={step.description || ''} />;
        }
        if (step.type === 'text') {
            return <input type="text" value={formData[fieldName] || ''} onChange={e => handleChange(fieldName, e.target.value)} className="w-full p-3 rounded-lg border-2 bg-transparent focus:outline-none" style={{ borderColor: hexToRgba(s.accent, 0.3), color: s.text }} placeholder={step.description || ''} />;
        }
        // radio (default)
        return (
            <div className="grid gap-3">
                {options.map((opt, i) => (
                    <label key={i} onClick={() => handleChange(fieldName, opt)}
                        className="flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all"
                        style={{ borderColor: formData[fieldName] === opt ? s.accent : s.dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)', backgroundColor: formData[fieldName] === opt ? hexToRgba(s.accent, 0.1) : 'transparent' }}>
                        <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center" style={{ borderColor: formData[fieldName] === opt ? s.accent : s.dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>
                            {formData[fieldName] === opt && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.accent }} />}
                        </div>
                        <span style={{ color: s.text }}>{opt}</span>
                    </label>
                ))}
            </div>
        );
    };

    const progressBar = (
        <div className="flex items-center justify-center gap-2 mb-6">
            {steps.map((_, i) => (
                <div key={i} className="h-2 rounded-full transition-all" style={{ width: i === currentStep ? '2rem' : '0.5rem', backgroundColor: i <= currentStep ? s.accent : hexToRgba(s.accent, 0.2) }} />
            ))}
            <span className="ml-3 text-sm" style={{ color: s.textSub }}>Paso {currentStep + 1} de {steps.length}</span>
        </div>
    );

    const successView = (
        <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center animate-bounce" style={{ backgroundColor: hexToRgba(s.accent, 0.15) }}>
                <svg className="w-8 h-8" fill="none" stroke={s.accent} strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
            </div>
            <p className="text-xl font-semibold" style={{ color: s.text }}>{successMessage}</p>
        </div>
    );

    const formContent = submitted ? successView : steps.length === 0 ? (
        <p style={{ color: s.textSub }} className="text-center py-8">Configura los pasos del formulario en el editor.</p>
    ) : (
        <div>
            {progressBar}
            <h3 className="text-lg font-semibold mb-1" style={{ color: s.text }}>{steps[currentStep]?.title}</h3>
            {steps[currentStep]?.description && <p className="text-sm mb-4" style={{ color: s.textSub }}>{steps[currentStep].description}</p>}
            <div className="mb-6">{renderStepInput(steps[currentStep] || {})}</div>
            <div className="flex justify-between">
                {currentStep > 0 ? (
                    <button onClick={() => setCurrentStep(c => c - 1)} className="px-5 py-2 rounded-lg font-medium transition-colors" style={{ color: s.accent, border: `1px solid ${s.accent}` }}>Anterior</button>
                ) : <span />}
                {currentStep < steps.length - 1 ? (
                    <button onClick={() => setCurrentStep(c => c + 1)} className="px-5 py-2 rounded-lg font-medium text-white transition-colors" style={{ backgroundColor: s.accent }}>{`Siguiente`}</button>
                ) : (
                    <button onClick={handleSubmit} disabled={loading} className="px-6 py-2 rounded-lg font-medium text-white transition-colors disabled:opacity-50" style={{ backgroundColor: s.accent }}>{loading ? '...' : submitText}</button>
                )}
            </div>
        </div>
    );

    const formCard = (
        <div className={variant === 'minimal' ? '' : 'rounded-2xl p-6 md:p-8'} style={variant === 'minimal' ? {} : { backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
            {formContent}
        </div>
    );

    return (
        <section ref={ref} className={anim} style={{ backgroundColor: s.bg }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div className="max-w-6xl mx-auto px-4 py-16 md:py-24">
                {variant === 'split' ? (
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} s={s} primaryColor={primaryColor} secondaryColor={secondaryColor} />
                        </div>
                        <div>{formCard}</div>
                    </div>
                ) : (
                    <div className={variant === 'centered' ? 'max-w-lg mx-auto' : 'max-w-xl mx-auto'}>
                        <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} s={s} primaryColor={primaryColor} secondaryColor={secondaryColor} align="center" />
                        <div className="mt-8">{formCard}</div>
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
